import axios from 'axios';
import {
  CustomerDetail,
  CustomerProfileUpdatePayload,
  CustomerListResponse,
  CustomerSearchParams,
  PaymentHistoryListResponse,
  PaymentHistoryParams,
  KeywordSettingListResponse,
  KeywordSettingParams,
  PointHistoryListResponse,
  PointHistoryParams,
  KeywordDataResponse,
  IpAllowlistListResponse,
  IpAllowlistItem,
} from '../models/CustomerModels';
import { setupApiInterceptors } from '../utils/apiInterceptor';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

// 환경변수에서 API 기본 URL 가져오기
const API_BASE_URL = getApiBaseUrl();
const API_PREFIX = '/api'; // API 프록시 경로

// Axios 인스턴스 생성
const customerApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 공통 인터셉터 설정
setupApiInterceptors(customerApi);

// 고객 관리 서비스 클래스
export class CustomerService {

  // 고객 목록 조회
  static async getCustomers(params: CustomerSearchParams = {}): Promise<CustomerListResponse> {
    try {
      const { page = 1, limit = 15, q } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      // 검색어가 있는 경우 추가
      if (q && q.trim()) {
        queryParams.append('q', q.trim());
      }

      console.log('고객 목록 API 호출:', {
        url: `/customers?${queryParams.toString()}`,
        params: { page, limit, q }
      });

      const response = await customerApi.get(`/customers?${queryParams.toString()}`);

      console.log('고객 목록 API 응답 성공:', response.data);

      // API 응답이 배열인 경우 변환
      if (Array.isArray(response.data)) {
        return {
          customers: response.data,
          total: response.data.length,
          page: page,
          limit: limit,
          totalPages: Math.ceil(response.data.length / limit)
        };
      }

      // API 응답이 객체인 경우 올바른 구조로 변환
      const apiData = response.data;
      console.log('🔍 API 응답 데이터 변환:', {
        원본데이터: apiData,
        items: apiData.items,
        itemsLength: apiData.items?.length,
        total: apiData.total,
        page: apiData.page,
        limit: apiData.limit,
        total_pages: apiData.total_pages
      });

      // items 배열이 존재하고 배열인지 확인
      const customersArray = Array.isArray(apiData.items) ? apiData.items : [];

      const transformedData = {
        customers: customersArray,
        total: apiData.total || 0,
        page: apiData.page || page,
        limit: apiData.limit || limit,
        totalPages: apiData.total_pages || Math.ceil((apiData.total || 0) / (apiData.limit || limit))
      };

      console.log('✅ 변환된 데이터:', {
        customers: transformedData.customers,
        customersLength: transformedData.customers.length,
        total: transformedData.total,
        page: transformedData.page,
        limit: transformedData.limit,
        totalPages: transformedData.totalPages
      });

      return transformedData;
    } catch (error: any) {
      console.error('고객 목록 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // 고객 상세 정보 조회
  static async getCustomerDetail(userIdx: number): Promise<CustomerDetail> {
    try {
      console.log('고객 상세 정보 API 호출:', {
        url: `/customers/${userIdx}`,
        userIdx
      });

      const response = await customerApi.get(`/customers/${userIdx}`);

      console.log('고객 상세 정보 API 응답 성공:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('고객 상세 정보 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  /** 고객 이름·사업자등록증·법인등록증 수정 후 갱신된 상세 정보 반환 */
  static async patchCustomerProfile(
    userIdx: number,
    payload: CustomerProfileUpdatePayload
  ): Promise<CustomerDetail> {
    const response = await customerApi.patch(`/customers/${userIdx}`, payload);
    return response.data as CustomerDetail;
  }

  // 고객 결제 내역 조회
  static async getPaymentHistory(params: PaymentHistoryParams): Promise<PaymentHistoryListResponse> {
    try {
      const { userIdx, page = 1, limit = 15 } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      console.log('고객 결제 내역 API 호출:', {
        url: `/customers/${userIdx}/payments?${queryParams.toString()}`,
        params: { userIdx, page, limit }
      });

      const response = await customerApi.get(`/customers/${userIdx}/payments?${queryParams.toString()}`);

      console.log('고객 결제 내역 API 응답 성공:', response.data);

      // API 응답이 배열인 경우 변환
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          total: response.data.length,
          page: page,
          limit: limit,
          totalPages: Math.ceil(response.data.length / limit)
        };
      }

      // API 응답이 객체인 경우 그대로 반환
      return response.data;
    } catch (error: any) {
      console.error('고객 결제 내역 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // 고객 키워드 설정 조회
  static async getKeywordSettings(params: KeywordSettingParams): Promise<KeywordSettingListResponse> {
    try {
      const { userIdx, page = 1, limit = 15 } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      console.log('고객 키워드 설정 API 호출:', {
        url: `/customers/${userIdx}/keywords?${queryParams.toString()}`,
        params: { userIdx, page, limit }
      });

      const response = await customerApi.get(`/customers/${userIdx}/keywords?${queryParams.toString()}`);

      console.log('고객 키워드 설정 API 응답 성공:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('고객 키워드 설정 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // 특정 키워드의 수집 데이터 조회 (신규 API)
  static async getKeywordData(params: {
    userIdx: number;
    keywordIdx: number;
    page?: number;
    limit?: number;
  }): Promise<KeywordDataResponse> {
    try {
      const { userIdx, keywordIdx, page = 1, limit = 15 } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const url = `/customers/${userIdx}/keywords/${keywordIdx}/data?${queryParams.toString()}`;

      console.log('키워드 수집 데이터 API 호출:', {
        url,
        params: { userIdx, keywordIdx, page, limit },
      });

      const response = await customerApi.get(url);

      console.log('키워드 수집 데이터 API 응답 성공:', response.data);

      return response.data as KeywordDataResponse;
    } catch (error: any) {
      console.error('키워드 수집 데이터 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
      });
      throw error;
    }
  }

  // 고객 포인트 내역 조회
  static async getPointHistory(params: PointHistoryParams): Promise<PointHistoryListResponse> {
    try {
      const { userIdx, page = 1, limit = 15 } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      console.log('고객 포인트 내역 API 호출:', {
        url: `/customers/${userIdx}/points?${queryParams.toString()}`,
        params: { userIdx, page, limit }
      });

      const response = await customerApi.get(`/customers/${userIdx}/points?${queryParams.toString()}`);

      console.log('고객 포인트 내역 API 응답 성공:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('고객 포인트 내역 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // 포인트 충전/차감 API
  static async updatePoints(params: {
    userIdx: number;
    pointType: boolean; // true: 충전, false: 차감
    pointAmount: number;
    pointDesc: string;
  }): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const { userIdx, pointType, pointAmount, pointDesc } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        point_type: pointType.toString(),
        point_amount: pointAmount.toString(),
        point_desc: pointDesc,
      });

      console.log('포인트 충전/차감 API 호출:', {
        url: `/customers/${userIdx}/points?${queryParams.toString()}`,
        params: { userIdx, pointType, pointAmount, pointDesc }
      });

      const response = await customerApi.post(`/customers/${userIdx}/points?${queryParams.toString()}`);

      console.log('포인트 충전/차감 API 응답 성공:', response.data);

      return {
        success: true,
        message: pointType ? '포인트가 성공적으로 충전되었습니다.' : '포인트가 성공적으로 차감되었습니다.',
        data: response.data
      };
    } catch (error: any) {
      console.error('포인트 충전/차감 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });

      const errorMessage = error.response?.data?.message ||
        (error.response?.status === 401 ? '인증이 필요합니다.' :
          error.response?.status === 403 ? '권한이 없습니다.' :
            error.response?.status === 404 ? '고객을 찾을 수 없습니다.' :
              error.response?.status === 400 ? '잘못된 요청입니다.' :
                '포인트 처리 중 오류가 발생했습니다.');

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  static async getIpAllowlist(
    userIdx: number,
    params: {
      page?: number;
      limit?: number;
      sort?: 'create_asc' | 'create_desc';
      status?: 'active' | 'all';
    } = {}
  ): Promise<IpAllowlistListResponse> {
    const { page = 1, limit = 20, sort = 'create_asc', status = 'active' } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      sort,
      status,
    });
    const response = await customerApi.get(
      `/customers/${userIdx}/ip-allowlist?${queryParams.toString()}`
    );
    return response.data;
  }

  static async postIpAllowlist(
    userIdx: number,
    cidr: string
  ): Promise<Record<string, unknown>> {
    const response = await customerApi.post(`/customers/${userIdx}/ip-allowlist`, { cidr });
    return response.data;
  }

  static async deleteIpAllowlist(
    userIdx: number,
    allowlistIdx: number
  ): Promise<Record<string, unknown>> {
    const response = await customerApi.delete(
      `/customers/${userIdx}/ip-allowlist/${allowlistIdx}`
    );
    return response.data;
  }

  static async patchIpAllowlist(
    userIdx: number,
    allowlistIdx: number,
    cidr: string
  ): Promise<IpAllowlistItem> {
    const response = await customerApi.patch<IpAllowlistItem>(
      `/customers/${userIdx}/ip-allowlist/${allowlistIdx}`,
      { cidr }
    );
    return response.data;
  }

  static async restoreIpAllowlist(
    userIdx: number,
    allowlistIdx: number
  ): Promise<Record<string, unknown>> {
    const response = await customerApi.post(
      `/customers/${userIdx}/ip-allowlist/${allowlistIdx}/restore`
    );
    return response.data;
  }
} 